import { DeleteOutline } from '@mui/icons-material';
import { Box, Card, Divider, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormPhoneNumField from '../../../FormFields/FormPhoneNumField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { TRANSPORTATION } from '../../../formQuestionsEnum';
import { formCardStyle, groupedFieldsStyle } from '../../../TransitionStyles';
import { validateEmail } from '../../../ValidateFunctions';

UsersForm.propTypes = {
  permissions: PropTypes.object,
};

export default function UsersForm({ permissions }) {
  const { control, setValue } = useFormContext();
  const [focusedField, setFocusedField] = useState(null);
  const {
    data: { isCanceled, transportationUserAccessTypesList },
  } = useSelector((state) => state.transitions);

  const baseFieldName = TRANSPORTATION.USER_LIST.fieldName;
  const isEditable = permissions?.[TRANSPORTATION.USER_LIST.id];
  const disabled = !isEditable || isCanceled;

  const { fields, append, remove } = useFieldArray({
    control,
    name: baseFieldName,
    keyName: '_id',
  });

  const watchedValues = useWatch({ control, name: baseFieldName }) || [];
  const visibleRows = fields.map((field, idx) => ({ field, idx })).filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  const handleDelete = (idx) => {
    const userId = watchedValues[idx]?.id;

    if (!userId) {
      remove(idx);
      return;
    }

    setValue(`${baseFieldName}[${idx}].deleteFlag`, true, { shouldDirty: true });
  };

  return (
    TRANSPORTATION.USER_LIST.id in permissions && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Users
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid item xs={12} sx={{ mb: 1 }}>
          <FormTitle fieldAttributes={{ questionTitle: 'User Information', fieldType: 'title' }} />
        </Grid>

        {visibleRows.map(({ field, idx }) => (
          <Grid container item xs={12} wrap="nowrap" key={field._id} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={visibleRows.length > 1 ? 11.5 : 12}>
              <Box sx={{ backgroundColor: '#f1f3f4cc', ...groupedFieldsStyle }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'First name' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].firstName`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Last name' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].lastName`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Email' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].email`}
                        fieldAttributes={{
                          isEditable,
                          validation: { pattern: { ...validateEmail } },
                        }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Phone number' }} />
                      <FormPhoneNumField
                        fieldName={`${baseFieldName}[${idx}].phoneNumber`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12}>
                    <FormTitle fieldAttributes={{ questionTitle: 'Access type' }} />
                    <FormButtonOptions
                      fieldName={`${baseFieldName}[${idx}].transportationAccessTypeId`}
                      fieldAttributes={{
                        isEditable,
                        options: transportationUserAccessTypesList,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {visibleRows.length > 1 && (
              <Grid item xs={0.5}>
                <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                  <DeleteOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        <Grid item xs={12}>
          <AddMoreButton
            buttonLabel="Add more"
            handleAddMoreClick={() =>
              append({
                id: 0,
                firstName: null,
                lastName: null,
                email: null,
                phoneNumber: null,
                transportationAccessTypeId: null,
              })
            }
            disabled={disabled}
          />
        </Grid>
      </Card>
    )
  );
}
